(function(window) {
    'use strict'

    const internal = window.RTAnalyticsInternal
    const config = internal.config
    let databasePromise = null

    function openDatabase() {
        if (!window.indexedDB) {
            return Promise.resolve(null)
        }

        if (databasePromise) {
            return databasePromise
        }

        databasePromise = new Promise(function(resolve) {
            let settled = false
            let request

            function complete(database) {
                if (settled) {
                    if (database) {
                        database.close()
                    }
                    return
                }

                settled = true
                resolve(database)
            }

            try {
                request = window.indexedDB.open(config.databaseName, config.databaseVersion)
            } catch (error) {
                databasePromise = null
                complete(null)
                return
            }

            request.onupgradeneeded = function() {
                const database = request.result
                let eventStore

                if (!database.objectStoreNames.contains(config.eventStoreName)) {
                    eventStore = database.createObjectStore(config.eventStoreName, {keyPath: 'id'})
                } else {
                    eventStore = request.transaction.objectStore(config.eventStoreName)
                }

                if (!eventStore.indexNames.contains('expiresAt')) {
                    eventStore.createIndex('expiresAt', 'expiresAt', {unique: false})
                }

                if (!database.objectStoreNames.contains(config.deliveryStoreName)) {
                    const deliveryStore = database.createObjectStore(config.deliveryStoreName, {keyPath: 'id'})
                    deliveryStore.createIndex('status', 'status', {unique: false})
                    deliveryStore.createIndex('eventId', 'eventId', {unique: false})
                    deliveryStore.createIndex('eventStatus', ['eventId', 'status'], {unique: false})
                    deliveryStore.createIndex('expiresAt', 'expiresAt', {unique: false})
                }
            }

            request.onsuccess = function() {
                const database = request.result
                database.onversionchange = function() {
                    database.close()
                    databasePromise = null
                }
                complete(database)
            }

            request.onerror = function() {
                databasePromise = null
                complete(null)
            }

            request.onblocked = function() {
                databasePromise = null
                complete(null)
            }
        })

        return databasePromise
    }

    function eventExists(database, eventId) {
        return new Promise(function(resolve) {
            let request

            try {
                const transaction = database.transaction(config.eventStoreName, 'readonly')
                request = transaction.objectStore(config.eventStoreName).get(eventId)
            } catch (error) {
                resolve(false)
                return
            }

            request.onsuccess = function() {
                resolve(!!request.result)
            }
            request.onerror = function() {
                resolve(false)
            }
        })
    }

    function persistEnvelope(event, deliveries) {
        if (!event || !deliveries.length) {
            return Promise.resolve({stored: false, existing: false})
        }

        return openDatabase().then(function(database) {
            if (!database) {
                return {stored: false, existing: false}
            }

            return new Promise(function(resolve) {
                let transaction

                try {
                    transaction = database.transaction(
                        [config.eventStoreName, config.deliveryStoreName],
                        'readwrite'
                    )
                    transaction.objectStore(config.eventStoreName).add(event)

                    const deliveryStore = transaction.objectStore(config.deliveryStoreName)
                    deliveries.forEach(function(delivery) {
                        deliveryStore.add(delivery)
                    })
                } catch (error) {
                    resolve({stored: false, existing: false})
                    return
                }

                transaction.oncomplete = function() {
                    resolve({stored: true, existing: false})
                }
                transaction.onerror = function() {}
                transaction.onabort = function() {
                    eventExists(database, event.id).then(function(exists) {
                        resolve({stored: exists, existing: exists})
                    })
                }
            })
        })
    }

    function getQueuedDeliveries(limit, eventId) {
        return openDatabase().then(function(database) {
            if (!database) {
                return []
            }

            return new Promise(function(resolve) {
                const deliveries = []
                let transaction
                let request

                try {
                    transaction = database.transaction(config.deliveryStoreName, 'readonly')
                    const store = transaction.objectStore(config.deliveryStoreName)
                    if (eventId) {
                        request = store.index('eventStatus').openCursor(
                            window.IDBKeyRange.only([eventId, 'queued'])
                        )
                    } else {
                        request = store.index('status').openCursor(window.IDBKeyRange.only('queued'))
                    }

                    request.onsuccess = function() {
                        const cursor = request.result
                        if (!cursor || deliveries.length >= limit) {
                            return
                        }

                        deliveries.push(cursor.value)
                        cursor.continue()
                    }
                } catch (error) {
                    resolve([])
                    return
                }

                transaction.oncomplete = function() {
                    resolve(deliveries)
                }
                transaction.onerror = function() {
                    resolve([])
                }
                transaction.onabort = function() {
                    resolve([])
                }
            })
        })
    }

    function claimDelivery(deliveryId) {
        return openDatabase().then(function(database) {
            if (!database) {
                return null
            }

            return new Promise(function(resolve) {
                let claimedDelivery = null
                let transaction

                try {
                    transaction = database.transaction(config.deliveryStoreName, 'readwrite')
                    const store = transaction.objectStore(config.deliveryStoreName)
                    const request = store.get(deliveryId)

                    request.onsuccess = function() {
                        const delivery = request.result
                        if (!delivery || delivery.status !== 'queued') {
                            return
                        }

                        delivery.status = 'claimed'
                        delivery.claimedAt = Date.now()
                        store.put(delivery)
                        claimedDelivery = delivery
                    }
                } catch (error) {
                    resolve(null)
                    return
                }

                transaction.oncomplete = function() {
                    resolve(claimedDelivery)
                }
                transaction.onerror = function() {
                    resolve(null)
                }
                transaction.onabort = function() {
                    resolve(null)
                }
            })
        })
    }

    function markAttempted(deliveryId, dispatched) {
        return openDatabase().then(function(database) {
            if (!database) {
                return false
            }

            return new Promise(function(resolve) {
                let transaction
                let updated = false

                try {
                    transaction = database.transaction(config.deliveryStoreName, 'readwrite')
                    const store = transaction.objectStore(config.deliveryStoreName)
                    const request = store.get(deliveryId)

                    request.onsuccess = function() {
                        const delivery = request.result
                        if (!delivery || delivery.status !== 'claimed') {
                            return
                        }

                        delivery.status = 'attempted'
                        delivery.attemptedAt = Date.now()
                        delivery.dispatched = !!dispatched
                        store.put(delivery)
                        updated = true
                    }
                } catch (error) {
                    resolve(false)
                    return
                }

                transaction.oncomplete = function() {
                    resolve(updated)
                }
                transaction.onerror = function() {
                    resolve(false)
                }
                transaction.onabort = function() {
                    resolve(false)
                }
            })
        })
    }

    function cleanupExpired() {
        return openDatabase().then(function(database) {
            if (!database) {
                return false
            }

            return new Promise(function(resolve) {
                let transaction

                try {
                    transaction = database.transaction(
                        [config.eventStoreName, config.deliveryStoreName],
                        'readwrite'
                    )

                    const expirationTime = Date.now()
                    const stores = [
                        transaction.objectStore(config.eventStoreName),
                        transaction.objectStore(config.deliveryStoreName)
                    ]

                    stores.forEach(function(store) {
                        const request = store.openCursor()
                        request.onsuccess = function() {
                            const cursor = request.result
                            if (!cursor) {
                                return
                            }

                            const value = cursor.value
                            const expiresAt = value.expiresAt || value.createdAt + config.eventTtlMs
                            if (expiresAt <= expirationTime) {
                                cursor.delete()
                            }
                            cursor.continue()
                        }
                    })
                } catch (error) {
                    resolve(false)
                    return
                }

                transaction.oncomplete = function() {
                    resolve(true)
                }
                transaction.onerror = function() {
                    resolve(false)
                }
                transaction.onabort = function() {
                    resolve(false)
                }
            })
        })
    }

    internal.storage = Object.freeze({
        persistEnvelope: persistEnvelope,
        getQueuedDeliveries: getQueuedDeliveries,
        claimDelivery: claimDelivery,
        markAttempted: markAttempted,
        cleanupExpired: cleanupExpired
    })
})(window)
